import React, { useEffect, useState } from "react"
import { FlatList, Image, ActivityIndicator, type ViewStyle } from "react-native"
import { ManageCompetitionApi, ManageCompetitionHelpers } from "../api"
import { Icons, Toggle } from "../../Components"
import type { BracketCompetitionProps, CompetitionPlayerBracketProps, CompetitionPlayerProps, CompetitionProps, CompetitionRecordProps, CompetitionResultProps, CompetitionSeasonPlayerProps, CompetitionSeasonProps, CompetitionSeasonResultProps, PlayerBracketProps, SquaresCompetitionProps } from "../../types"
import { Button, Text, View } from "../../Components/Themed"
import { useColors } from "../../constants/useColors"
import Pagination from "../../Components/Pagination"

type AdminCompetitionListProps = {
    width:number,
    header_style?:ViewStyle,
    footer_style?:ViewStyle,
    float?:boolean,
    height?:number,
    onSelectCompetition:(competition:CompetitionProps) => void,
    onSelectSquaresCompetition:(squares_competition:SquaresCompetitionProps) => void,
    onSelectBracketCompetition:(bracket_competition:BracketCompetitionProps) => void,
    onClose: () => void,
    onCreateNew: (engagement_type:'competitions'|'brackets'|'squares') => void
}
const sections = ['header','toggle', 'active_past_toggle', 'pagination', 'loading', 'admin_competitions','competitions','admin_squares','squares', 'admin_brackets', 'brackets']
const AdminCompetitionList = ({ header_style, float, footer_style, onClose, onCreateNew, onSelectCompetition, onSelectBracketCompetition, onSelectSquaresCompetition }:AdminCompetitionListProps) => {
    const Colors = useColors();
    const [ list_data, setData ] = useState<{
        loading: boolean,
        active_engagement: 'competitions'|'squares'|'brackets'
        active_tab:'active'|'closed',
        offset:number,
        show_admin_competitions:boolean,
        show_admin_squares:boolean,
        show_admin_brackets:boolean,
        admin_competitions:CompetitionProps[],
        admin_squares:SquaresCompetitionProps[],
        admin_brackets:BracketCompetitionProps[],
        competitions:CompetitionProps[],
        competition_players:CompetitionPlayerProps[],
        competition_records:CompetitionRecordProps[],
        competition_results:CompetitionResultProps[],
        competition_seasons:CompetitionSeasonProps[],
        competition_season_players:CompetitionSeasonPlayerProps[],
        competition_season_results:CompetitionSeasonResultProps[],
        bracket_competitions:BracketCompetitionProps[],
        competition_player_brackets:CompetitionPlayerBracketProps[],
        player_brackets:PlayerBracketProps[],
        squares_competitions:SquaresCompetitionProps[]
    }>({
        loading:false,
        offset:0,
        active_engagement: 'competitions',
        active_tab: 'active',
        competitions: [],
        competition_players:[],
        competition_records:[],
        show_admin_brackets: false,
        show_admin_competitions: false,
        show_admin_squares: false,
        competition_results:[],
        admin_brackets:[],
        admin_competitions:[],
        admin_squares:[],
        competition_seasons: [],
        competition_season_players:[],
        competition_season_results:[],
        bracket_competitions: [],
        competition_player_brackets:[],
        player_brackets: [],
        squares_competitions: []
    })
    const { offset, active_tab, active_engagement, show_admin_brackets, show_admin_competitions, show_admin_squares, admin_squares, admin_brackets, loading, competitions, admin_competitions, competition_results, bracket_competitions, squares_competitions, competition_player_brackets } = list_data;

    useEffect(() => {
        ManageCompetitionApi.setEnvironment();
        getDataFromServer(0)
    },[])

    useEffect(() => {
        getDataFromServer(0)
    },[active_engagement, active_tab])

    const getDataFromServer = async(offset:number) => {
        setData({ ...list_data, loading:true });
        switch(active_engagement){
            case 'competitions':
                const comps = await ManageCompetitionApi.getMyAdminCompetition(offset, active_tab);
                if(active_tab == 'active'){
                    const c_resp = await ManageCompetitionApi.getMyActiveCompetitions();
                    setData({ 
                        ...list_data,  
                        loading: false, 
                        admin_competitions: comps,
                        competitions: c_resp.competitions,
                        competition_players: c_resp.competition_players,
                        competition_records: c_resp.competition_records,
                        competition_results: c_resp.competition_results,
                        offset
                    })
                } else {
                    const c_resp = await ManageCompetitionApi.getMyHistoryCompetitions(offset);
                    setData({ 
                        ...list_data,  
                        loading: false, 
                        admin_competitions: comps,
                        competitions: c_resp.competitions,
                        competition_players: c_resp.competition_players,
                        competition_records: c_resp.competition_records,
                        competition_results: c_resp.competition_results,
                        offset
                    })
                }
                return
            case 'brackets':
                if(active_tab == 'active'){
                    const admin_bracks_active = await ManageCompetitionApi.getMyActiveAdminBracketCompetitions()
                    const b_resp = await ManageCompetitionApi.getMyActiveBracketCompetitions();
                    setData({
                        ...list_data,
                        loading:false,
                        admin_brackets: admin_bracks_active,
                        bracket_competitions: b_resp.bracket_competitions,
                        player_brackets: b_resp.player_brackets,
                        competition_player_brackets: b_resp.competition_player_brackets,
                        offset
                    })
                } else {
                    const admin_bracks_history = await ManageCompetitionApi.getMyHistoryAdminBracketCompetitions(offset);
                    const b_resp = await ManageCompetitionApi.getMyHistoryBracketCompetitions(offset);
                    setData({
                        ...list_data,
                        loading:false,
                        admin_brackets: admin_bracks_history,
                        bracket_competitions: b_resp.bracket_competitions,
                        player_brackets: b_resp.player_brackets,
                        competition_player_brackets: b_resp.competition_player_brackets,
                        offset
                    })
                }
                return
            case 'squares':
                if(active_tab == 'active'){
                    const active_admin_squares = await ManageCompetitionApi.getMyActiveAdminSquares();
                    const s_resp = await ManageCompetitionApi.getMyActiveSquaresCompetitions();
                    setData({
                        ...list_data,
                        loading:false,
                        offset,
                        admin_squares: active_admin_squares,
                        squares_competitions: s_resp.squares_competitions
                    })
                } else {
                    const past_admin_squares = await ManageCompetitionApi.getMyHistoryAdminSquares(offset);
                    const s_resp = await ManageCompetitionApi.getMyHistorySquaresCompetitions(offset);
                    setData({
                        ...list_data,
                        loading:false,
                        admin_squares: past_admin_squares,
                        offset,
                        squares_competitions: s_resp.squares_competitions
                    })
                }
                return
            default: return
        }  
    }

    const renderCompetitions = (data: { item:CompetitionProps, index:number }) => {
        const competition_result = competition_results.find(cr => cr.competition_id == data.item.competition_id);
        const cl = data.item.market_type == 'FOR_MONEY' ? '$' : 'E'
        return (
            <Button style={{ flexDirection:'row', alignItems:'center', borderRadius:0, padding:15, borderBottomWidth:1, borderColor:Colors.borders.light }} onPress={() => onSelectCompetition(data.item)}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode="cover"
                />
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme="h2">{data.item.competition_name}</Text>
                    <Text style={{ marginTop:3 }} theme="description">{data.item.competition_description}</Text>
                </View>
                {competition_result ?
                <View style={{ marginRight:10, marginLeft:10, justifyContent:'center' }}>
                    <Text size={12}>{ManageCompetitionHelpers.formatPlace(competition_result.place)}</Text>
                    <Text size={12} style={{ marginTop:3 }}>{cl}{competition_result.winnings.toFixed(2)}</Text>
                </View>
                :<></>}
                <Icons.ChevronIcon direction='right' size={8} color={Colors.text.h1}/>
            </Button>
        )
    }

    const renderSquares = (data: { item:SquaresCompetitionProps, index:number }) => {
        return (
            <Button style={{ flexDirection:'row', alignItems:'center', borderRadius:0, padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }} onPress={() => onSelectSquaresCompetition(data.item)}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode="cover"
                />
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme="h2">{data.item.sq_comp_name}</Text>
                    <Text style={{ marginTop:3 }} theme="description">{data.item.sq_comp_description}</Text>
                </View>
                <Icons.ChevronIcon direction='right' size={8} color={Colors.text.h1}/>
            </Button>
        )
    }

    const renderBrackets = (data: { item:BracketCompetitionProps, index:number }) => {
        const competition_player_bracket = competition_player_brackets.find(cpb => cpb.bracket_competition_id == data.item.bracket_competition_id);
        const cl = data.item.market_type == 'FOR_MONEY' ? '$' : 'E'
        return (
            <Button style={{ flexDirection:'row', alignItems:'center', borderRadius:0, padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }} onPress={() => onSelectBracketCompetition(data.item)}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode="cover"
                />
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme="h2">{data.item.competition_name}</Text>
                    <Text style={{ marginTop:3 }} theme="description">{data.item.competition_description}</Text>
                </View>
                {competition_player_bracket ?
                <View style={{ marginRight:10, marginLeft:10, justifyContent:'center' }}>
                    <Text size={12}>{ManageCompetitionHelpers.formatPlace(competition_player_bracket.place)}</Text>
                    <Text size={12} style={{ marginTop:3 }}>{cl}{competition_player_bracket.winnings.toFixed(2)}</Text>
                </View>
                :<></>}
                <Icons.ChevronIcon direction='right' size={8} color={Colors.text.h1}/>
            </Button>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, ...header_style }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme="h1">My Admin Engagements</Text>
                            <Text theme="description" style={{ marginTop:3 }}>Use the buttons below to see your active and past engagements (competitions, brackets, squares, etc)</Text>
                        </View>
                        <Button
                            title="X"
                            float
                            title_color={Colors.text.error}
                            onPress={() => onClose()}
                        />
                    </View>
                )
            case 'toggle':
                return (
                    <View style={{padding:10}}>
                        <Toggle
                            options={[
                                { key: 'competitions', label:'Competitions' },
                                { key: 'squares', label: 'Squares' },
                                { key: 'brackets', label: 'Brackets' }
                            ]}
                            selected_option={active_engagement}
                            onSelectOption={(o) => setData({ ...list_data, active_engagement: o as 'competitions'|'brackets'|'squares' })}
                        />
                    </View>
                )
            case 'active_past_toggle':
                return (
                    <View style={{ padding:10 }}>
                        <Toggle
                            options={[
                                { key: 'closed', label:'Closed' },
                                { key: 'active', label: 'Active' }
                            ]}
                            selected_option={active_tab}
                            onSelectOption={(o) => setData({ ...list_data, active_tab: o as 'active'|'closed' })}
                        />
                    </View>
                )
            case 'pagination':
                if(active_tab != 'closed'){ return <></> }
                return (
                    <View transparent>
                        <Pagination
                            offset={offset}
                            onNext={() => getDataFromServer(offset + 1)}
                            onPrevious={() => getDataFromServer(offset - 1)}
                        />
                    </View>
                )
            case 'loading':
                if(!loading){ return <></> }
                return (
                    <View transparent>
                        <ActivityIndicator style={{ alignSelf:'center', padding:20 }} size='large' color={Colors.text.h1} />
                    </View>
                )
            case 'admin_competitions':
                if(active_engagement != 'competitions'){ return <></> }
                if(admin_competitions.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme="h1">Admin Competitions</Text>
                                <Text theme="description" style={{ marginTop:3 }}>Below are the competitions that you are the admin of</Text>
                            </View>
                            <Button transparent onPress={() => setData({ ...list_data, show_admin_competitions:!show_admin_competitions })}>
                                <Icons.ChevronIcon size={10} direction={show_admin_competitions ? 'up' : 'down'} color={Colors.text.action} />
                            </Button>
                        </View>
                        {show_admin_competitions ?
                        <FlatList
                            data={admin_competitions}
                            key={'admin_comps_list'}
                            keyExtractor={item => `admin_${item.competition_id}`}
                            renderItem={renderCompetitions}
                        />
                        :<></>}
                    </View>
                )
            case 'competitions':
                if(active_engagement != 'competitions'){ return <></> }
                if(competitions.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme="h1">My Competitions</Text>
                                <Text theme="description" style={{ marginTop:3 }}>Below are the competitions that you have joined</Text>
                            </View>
                        </View>
                        <FlatList
                            data={competitions}
                            key={'competitions_list'}
                            keyExtractor={item => item.competition_id.toString()}
                            renderItem={renderCompetitions}
                        />
                    </View>
                )
            case 'admin_squares':
                if(active_engagement != 'squares'){ return <></> }
                if(admin_squares.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme="h1">Admin Squares Competitions</Text>
                                <Text theme="description" style={{ marginTop:3 }}>Below are the squares competitions that you are the admin of</Text>
                            </View>
                            <Button transparent onPress={() => setData({ ...list_data, show_admin_squares:!show_admin_squares })}>
                                <Icons.ChevronIcon size={10} direction={show_admin_squares ? 'up' : 'down'} color={Colors.text.action} />
                            </Button>
                        </View>
                        {show_admin_squares ?
                        <FlatList
                            data={admin_squares}
                            key={'admin_squares_list'}
                            keyExtractor={item => `admin_${item.sq_comp_id}`}
                            renderItem={renderSquares}
                        />
                        :<></>}
                    </View>
                )
            case 'squares':
                if(active_engagement != 'squares'){ return <></> }
                if(squares_competitions.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme="h1">Squares Competitions</Text>
                                <Text theme="description" style={{ marginTop:3 }}>Below are the squares competitions that you are participating in</Text>
                            </View>
                        </View>
                        <FlatList
                            data={squares_competitions}
                            key={'squares_list'}
                            keyExtractor={item => `${item.sq_comp_id}`}
                            renderItem={renderSquares}
                        />
                    </View>
                )
            case 'admin_brackets':
                if(active_engagement != 'brackets'){ return <></> }
                if(admin_brackets.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme="h1">Admin Bracket Competitions</Text>
                                <Text theme="description" style={{ marginTop:3 }}>Below are the bracket competitions that you are the admin of</Text>
                            </View>
                            <Button transparent onPress={() => setData({ ...list_data, show_admin_brackets:!show_admin_brackets })}>
                                <Icons.ChevronIcon size={10} direction={show_admin_brackets ? 'up' : 'down'} color={Colors.text.action} />
                            </Button>
                        </View>
                        {show_admin_brackets ?
                        <FlatList
                            data={admin_brackets}
                            key={'admin_brackets_list'}
                            keyExtractor={item => `admin_${item.bracket_competition_id}`}
                            renderItem={renderBrackets}
                        />
                        :<></>}
                    </View>
                )
            case 'brackets':
                if(active_engagement != 'brackets'){ return <></> }
                if(bracket_competitions.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme="h1">Bracket Competitions</Text>
                                <Text theme="description" style={{ marginTop:3 }}>Below are the bracket competitions that you have participated in</Text>
                            </View>
                        </View>
                        <FlatList
                            data={bracket_competitions}
                            key={'brackets_list'}
                            keyExtractor={item => `${item.bracket_competition_id}`}
                            renderItem={renderBrackets}
                        />
                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View float={float} style={{ flex:1 }}>
            <View transparent style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'admin_list'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
            </View>
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, ...footer_style }}>
                <Button
                    style={{ flex:1 }}
                    onPress={() => onCreateNew(active_engagement)}
                    type="success"
                    title="CREATE NEW ENGAGEMENT"
                />
            </View>
        </View>
    )
}


export default AdminCompetitionList